import { MouseEvent as ReactMouseEvent } from "react";

import { faCakeCandles } from "@fortawesome/pro-light-svg-icons";

import { Icon } from "@/components";
import { TableColumn } from "@/components/Table/Table";
import { SortParams } from "@/types/types";

import { ContactProspect } from "../../schema/contacts";
import { getOrderStatus } from "../column-utils";

/**
 * Company-fact columns for the contacts grid, extracted from `allColumns.tsx` so that file keeps
 * shrinking under the 500-line ratchet. Re-exported from `allColumns`, which is where the contacts
 * table builds its column list.
 */

/** Shared copy-on-click cell; the grid row is itself clickable, so the events must not bubble. */
const CopyableCell = ({
	value,
	handleCopyClick,
	children,
}: {
	value: string | null | undefined;
	handleCopyClick: (value: string) => void;
	children: React.ReactNode;
}) => (
	<button
		className="text-sm text-gray-700 cursor-pointer"
		type="button"
		onClick={(e: ReactMouseEvent) => {
			e.preventDefault();
			e.stopPropagation();

			if (value) {
				handleCopyClick(value);
			}
		}}
	>
		{children}
	</button>
);

/**
 * The company name as a person would write it in an email (PREC-2748).
 *
 * Read-only and display-only: the value is resolved by the send path (and the backfill job), never on
 * a grid page load, so it falls back to the raw company name while it is still null.
 */
export const renderContactNormalizedCompanyNameColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.nameNormalized";

	const valueOf = (row: ContactProspect): string =>
		row.organization?.nameNormalized || row.organization?.name || "";

	return {
		title: ts("table.normalizedCompanyName"),
		width: "fit-content",
		minWidth: "150px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactNormalizedCompanyName",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => (
			<CopyableCell handleCopyClick={handleCopyClick} value={valueOf(row)}>
				{valueOf(row) || "-"}
			</CopyableCell>
		),
	};
};

export const renderContactCompanyIndustryColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.industry";

	return {
		title: ts("table.companyIndustry"),
		width: "fit-content",
		minWidth: "130px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactCompanyIndustry",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactProspect) => (
			<CopyableCell handleCopyClick={handleCopyClick} value={row.organization?.industry}>
				{row.organization?.industry || "-"}
			</CopyableCell>
		),
	};
};

export const renderContactCompanySizeColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.estimatedNumEmployees";

	return {
		title: ts("table.companySize"),
		width: "fit-content",
		minWidth: "110px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactCompanySize",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactProspect) => (
			<CopyableCell
				handleCopyClick={handleCopyClick}
				value={row.organization?.estimatedNumEmployees?.toLocaleString()}
			>
				{row.organization?.estimatedNumEmployees?.toLocaleString() || "-"}
			</CopyableCell>
		),
	};
};

export const renderContactCompanyFoundedYearColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.foundedYear";

	return {
		title: ts("table.companyFoundedYear"),
		width: "fit-content",
		minWidth: "110px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactCompanyFoundedYear",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => (
			<CopyableCell
				handleCopyClick={handleCopyClick}
				value={row.organization?.foundedYear?.toString()}
			>
				{row.organization?.foundedYear}{" "}
				{row.organization?.foundedYear && <Icon color="#374151" icon={faCakeCandles} />}
			</CopyableCell>
		),
	};
};
